import { getDocs, collection, query, where, doc, getDoc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';

export type Survey = {
    id: string;
    surveyId?: string;
    createdBy: string;
    dateOfVisit?: string;
    branchId?: string;
    companyId?: string;
    branch?: {
        id?: string;
        branchCode?: string;
        address?: string;
        branchName?: string;
        [key: string]: any;
    };
    companyDetails?: {
        companyName?: string;
        imgUrl?: string;
    };
    jointVisit?: string;
    jointTeams?: string[];
    cctv?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    bams?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    guarding?: {
        status?: string;
        issues?: string[];
        otherIssues?: string[];
    };
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
    status?: string;
    remarks?: string;
    createdAt?: any;
    updatedAt?: any;
    [key: string]: any;
};

export type Complaint = {
    id: string;
    complaintId?: string;
    surveyId?: string;
    createdBy: string;
    issueType?: string;
    branchId?: string;
    companyId?: string;
    surveyData?: Survey;
    status?: string;
    issues?: string[];
    customIssues?: Array<{
        heading?: string;
        description?: string;
    }>;
    description?: string;
    resolution?: string;
    targetCompanyName?: string;
    complaintCompanyDetails?: {
        companyName?: string;
    };
    createdAt?: any;
    updatedAt?: any;
    [key: string]: any;
};

async function fetchBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    if (isBankUser) {
        // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
        const directBank = await getDoc(doc(db, "banks", uid));
        if (directBank.exists()) {
            return uid;
        }
        // Try query by userId field
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const bankData = bankDoc.data();
            if (bankData.userId === uid) {
                return bankDoc.id;
            }
        }
        return null;
    } else {
        // For PSO users, find which bank has this PSO
        const banksSnap = await getDocs(collection(db, "banks"));
        for (const bankDoc of banksSnap.docs) {
            const posDocRef = doc(db, "banks", bankDoc.id, "pos", uid);
            const posDoc = await getDoc(posDocRef);
            if (posDoc.exists()) {
                return bankDoc.id;
            }
        }
        return null;
    }
}

export async function fetchSurveys(isBankUser: boolean = false): Promise<Survey[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        // Get bankId/companyId first
        let companyId = await fetchBankId(uid, isBankUser);
        // Fallback: if treated as bank user (e.g., MIS incharge) but not found via bank path,
        // try PSO lookup to resolve the company id
        if (!companyId && isBankUser) {
            companyId = await fetchBankId(uid, false);
        }
        if (!companyId) {
            console.log('No bank found for user:', uid);
            return [];
        }
        const resolvedCompanyId: string = companyId;

        // Fetch surveys from banks/{companyId}/visit
        // Bank users (and MIS treated as bank by caller) should see all surveys (no createdBy filter)
        const baseCollection = collection(db, "banks", resolvedCompanyId, "visit");
        const q = isBankUser
            ? baseCollection
            : query(baseCollection, where("createdBy", "==", uid));
        const visitSnap = await getDocs(q);

        console.log(`📊 Found ${visitSnap.docs.length} surveys`);

        // Process each survey and fetch branch/company details
        const surveysList = await Promise.all(
            visitSnap.docs.map(async (d) => {
                const data = d.data();
                let branch = null;
                let companyDetails = null;

                // Fetch branch data
                if (data.branchId) {
                    try {
                        const branchDoc = await getDoc(
                            doc(db, "banks", resolvedCompanyId, "branch", data.branchId)
                        );
                        if (branchDoc.exists()) {
                            branch = { id: branchDoc.id, ...branchDoc.data() };
                        }
                    } catch (error) {
                        console.error('Error fetching branch:', error);
                    }
                }

                // Fetch company details efficiently
                if (data.companyId) {
                    try {
                        // Try to get from bank document first (fastest)
                        const bankDoc = await getDoc(doc(db, "banks", data.companyId));
                        if (bankDoc.exists()) {
                            const bankData = bankDoc.data();
                            companyDetails = {
                                companyName: bankData.companyName || "N/A",
                                imgUrl: bankData.imgUrl || ""
                            };
                        } else {
                            // Fallback to companies collection
                            const companyDoc = await getDoc(doc(db, "companies", data.companyId));
                            if (companyDoc.exists()) {
                                const companyData = companyDoc.data();
                                companyDetails = {
                                    companyName: companyData.companyName || "N/A",
                                    imgUrl: companyData.imgUrl || ""
                                };
                            } else {
                                companyDetails = {
                                    companyName: "N/A",
                                    imgUrl: ""
                                };
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching company details:', error);
                        companyDetails = {
                            companyName: "N/A",
                            imgUrl: ""
                        };
                    }
                }

                const surveyData = {
                    id: d.id,
                    ...data,
                    branch,
                    companyDetails,
                    // Status will be calculated based on complaints in the component
                    // Don't set default here, let it be calculated dynamically
                } as Survey;

                return surveyData;
            })
        );

        // Sort by creation date (newest first)
        surveysList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        console.log('✅ Fetched Surveys:', surveysList.length);
        return surveysList;
    } catch (error) {
        console.error('Error fetching surveys:', error);
        return [];
    }
}

export async function fetchComplaints(isBankUser: boolean = false): Promise<Complaint[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        // Get bankId/companyId first
        let companyId = await fetchBankId(uid, isBankUser);
        // Fallback similar to fetchSurveys for MIS incharge treated as bank
        if (!companyId && isBankUser) {
            companyId = await fetchBankId(uid, false);
        }
        if (!companyId) {
            console.log('No bank found for user:', uid);
            return [];
        }
        const resolvedCompanyId: string = companyId;

        // First fetch all surveys to get survey IDs
        const surveys = await fetchSurveys(isBankUser);

        // Fetch complaints from all surveys in parallel
        const complaintPromises = surveys.map(async (survey) => {
            try {
                const complaintsSnap = await getDocs(
                    collection(db, "banks", resolvedCompanyId, "visit", survey.id, "complain")
                );
                return complaintsSnap.docs.map(complaintDoc => ({
                    doc: complaintDoc,
                    survey: survey
                }));
            } catch (error) {
                console.error(`Error fetching complaints for survey ${survey.id}:`, error);
                return [];
            }
        });

        // Wait for all complaint queries to complete
        const allComplaintDocs = await Promise.all(complaintPromises);
        const flatComplaintDocs = allComplaintDocs.flat();

        console.log(`📋 Found ${flatComplaintDocs.length} complaints`);

        // Process complaints
        const complaintsList: Complaint[] = flatComplaintDocs.map(({ doc: complaintDoc, survey }) => {
            const complaintData = complaintDoc.data();

            return {
                id: complaintDoc.id,
                surveyId: survey.id,
                surveyData: survey,
                // Explicitly map all complaint fields
                branchId: complaintData.branchId,
                companyId: complaintData.companyId,
                createdAt: complaintData.createdAt,
                createdBy: complaintData.createdBy,
                issueType: complaintData.issueType,
                issues: complaintData.issues || [],
                status: complaintData.status,
                targetCompanyName: complaintData.targetCompanyName,
                updatedAt: complaintData.updatedAt,
                customIssues: complaintData.customIssues || [],
                description: complaintData.description,
                resolution: complaintData.resolution,
                // Use targetCompanyName as company details (no additional fetching)
                complaintCompanyDetails: complaintData.targetCompanyName ? {
                    companyName: complaintData.targetCompanyName
                } : null,
                ...complaintData // Spread any additional fields
            } as Complaint;
        });

        // Sort by creation date (newest first)
        complaintsList.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        console.log('✅ Fetched Complaints:', complaintsList.length);
        return complaintsList;
    } catch (error) {
        console.error('Error fetching complaints:', error);
        return [];
    }
}

export async function updateSurvey(companyId: string, surveyId: string, updates: Partial<Survey>): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    try {
        const surveyRef = doc(db, "banks", companyId, "visit", surveyId);
        await updateDoc(surveyRef, {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating survey:', error);
        throw error;
    }
}

export async function getCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    return await fetchBankId(uid, isBankUser);
}

export async function createSurvey(companyId: string, surveyData: Partial<Survey>): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    try {
        const surveyRef = collection(db, "banks", companyId, "visit");
        const newSurvey = {
            ...surveyData,
            createdBy: uid,
            companyId: companyId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };
        const docRef = await addDoc(surveyRef, newSurvey);
        return docRef.id;
    } catch (error) {
        console.error('Error creating survey:', error);
        throw error;
    }
}

